import { queryClient } from '../../api'
import { BOOKINGS_QUERY_KEY } from '../booking'
import { TRANSACTIONS_QUERY_KEY } from '../transaction'
import { ACCOUNTS_QUERY_KEY } from '../account'
import { SALARY_ISSUES_QUERY_KEY } from '../salaryIssue'
import {
  CountedSalariesFullInfoByEmployeeTypeToParse,
  CountedSalariesFullInfoTypeToParse,
  EmployeeSalariesItemsTypeToParse,
} from './types'
import { toZonedTimeFromZonedUtc } from '@expane/date'
import {
  SALARY_EMPLOYEES_FULL_INFO_QUERY_KEY,
  SALARY_EMPLOYEES_ITEMS_QUERY_KEY,
  SALARY_FULL_INFO_QUERY_KEY,
} from './queryKeys'

export const invalidateOnPaySalary = async () => {
  await queryClient.invalidateQueries({
    predicate: query => query.queryKey.includes(SALARY_ISSUES_QUERY_KEY),
  })
  await queryClient.invalidateQueries([BOOKINGS_QUERY_KEY])
  await queryClient.invalidateQueries([TRANSACTIONS_QUERY_KEY])
  await queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY])
  await queryClient.invalidateQueries([SALARY_FULL_INFO_QUERY_KEY])
  await queryClient.invalidateQueries([SALARY_EMPLOYEES_FULL_INFO_QUERY_KEY])
  await queryClient.invalidateQueries([SALARY_EMPLOYEES_ITEMS_QUERY_KEY])
}

export const parseDatesInEmployeesFullSalaryInfoGqlResponse = <
  T extends CountedSalariesFullInfoByEmployeeTypeToParse,
>(
  salary: T,
  timezone: string,
): T => {
  if (salary.notPaidSalary) {
    if (salary.notPaidSalary.period.startPeriod) {
      salary.notPaidSalary.period.startPeriod = toZonedTimeFromZonedUtc(
        salary.notPaidSalary.period.startPeriod,
        timezone,
      )
    }
    if (salary.notPaidSalary.period.endPeriod) {
      salary.notPaidSalary.period.endPeriod = toZonedTimeFromZonedUtc(
        salary.notPaidSalary.period.endPeriod,
        timezone,
      )
    }
    for (const notPaidSalary of salary.notPaidSalary.notPaidRateSalaryRequestData) {
      if (notPaidSalary.startPeriod) {
        notPaidSalary.startPeriod = toZonedTimeFromZonedUtc(notPaidSalary.startPeriod, timezone)
      }
      if (notPaidSalary.endPeriod) {
        notPaidSalary.endPeriod = toZonedTimeFromZonedUtc(notPaidSalary.endPeriod, timezone)
      }
    }
  }
  if (salary.debtSalary) {
    if (salary.debtSalary.period.startPeriod) {
      salary.debtSalary.period.startPeriod = toZonedTimeFromZonedUtc(
        salary.debtSalary.period.startPeriod,
        timezone,
      )
    }
    if (salary.debtSalary.period.endPeriod) {
      salary.debtSalary.period.endPeriod = toZonedTimeFromZonedUtc(
        salary.debtSalary.period.endPeriod,
        timezone,
      )
    }

    for (const notPaidSalary of salary.debtSalary.notPaidRateSalaryRequestData) {
      if (notPaidSalary.startPeriod) {
        notPaidSalary.startPeriod = toZonedTimeFromZonedUtc(notPaidSalary.startPeriod, timezone)
      }
      if (notPaidSalary.endPeriod) {
        notPaidSalary.endPeriod = toZonedTimeFromZonedUtc(notPaidSalary.endPeriod, timezone)
      }
    }
  }

  return salary
}

export const parseDatesInFullSalaryInfoGqlResponse = <T extends CountedSalariesFullInfoTypeToParse>(
  data: T,
  timezone: string,
) => {
  if (data.salaries) {
    data.salaries = data.salaries.map(salary =>
      parseDatesInEmployeesFullSalaryInfoGqlResponse(salary, timezone),
    )
  }

  return data
}

export const parseDatesInEmployeeSalariesItemsGqlResponse = <
  T extends EmployeeSalariesItemsTypeToParse,
>(
  data: T,
  timezone: string,
) => {
  if (data.salaryItems) {
    for (const salaryItem of data.salaryItems) {
      if (salaryItem.date) salaryItem.date = toZonedTimeFromZonedUtc(salaryItem.date, timezone)
    }
  }

  if (data.referralPercentageSalaryItems) {
    for (const salaryItem of data.referralPercentageSalaryItems) {
      if (salaryItem.date) salaryItem.date = toZonedTimeFromZonedUtc(salaryItem.date, timezone)
    }
  }

  return data
}
